// Types AND values come from the published package the CDN serves — the merchant has nothing
// else (D-091). This import also REGISTERS the custom elements. The specifier stays EXTERNAL
// (rolldown.config.mjs) and the page's IMPORT MAP resolves it to the CDN, so the browser fetches
// ONE copy that every module shares (conventions §12). TypeScript maps the same name to the
// shipped payplug-elements.d.ts (tsconfig `paths`).
import { NetworkSessionClient, SUPPORTED_LANGUAGES, type PayplugSession, type StatusChangeDetail } from "@payplug/elements"; // prettier-ignore

// M10 European i18n playground (D-090), on the NETWORK stack since fin-isomorphisme lot 1: the
// m8 wiring (session created by the merchant backend, POST /m10/session — token only in the
// page, persisted across the redirect round-trip), but the language is the star. The
// <payplug-session> carries NO `locale` attribute — the elements default to navigator.language —
// and the toolbar offers a <select> over every supported language (too many for buttons),
// labeled with each language's endonym via Intl.DisplayNames. Every catalog is a lazy chunk
// (D-058): switching language fetches exactly one file. All methods here REDIRECT (the preset
// carries no hosted method): every journey finishes on the orchestrator's fake-PSP page, so the
// page needs no PSP panel — playing hosted verdicts is m8's job.

const TOKEN_KEY = "payplug-m10:token";
const ORCH_KEY = "payplug-m10:orchestrator";
const LOCALE_KEY = "payplug-m10:locale";

const FLAGS = [
  { id: "flag-partial", attr: "partial-payment", key: "payplug-m10:partial" },
  { id: "flag-cancel", attr: "individual-cancel", key: "payplug-m10:cancel" },
  { id: "flag-hide-cart-on-final", attr: "hide-cart-on-final", key: "payplug-m10:hide-cart-on-final" },
] as const;

/** A language's endonym ("Deutsch", "Ελληνικά", …): the language names ITSELF — that is how a
 *  customer scans the list for their own language. Intl.DisplayNames, zero data shipped. */
function endonym(lang: string): string {
  const name = new Intl.DisplayNames([lang], { type: "language" }).of(lang) ?? lang;
  return name.charAt(0).toLocaleUpperCase(lang) + name.slice(1);
}

/** Fetch a fresh session token from the merchant backend (this demo server). */
async function createSession(): Promise<{ elementsToken: string; orchestratorUrl: string }> {
  const response = await fetch("./m10/session", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({}),
  });
  if (!response.ok) throw new Error(`merchant backend answered ${response.status}`);
  return (await response.json()) as { elementsToken: string; orchestratorUrl: string };
}

async function main(): Promise<void> {
  // Token bootstrap: reuse the stored session across the redirect round-trip; otherwise ask
  // the merchant backend for a new one. "Nouvelle session" clears the storage.
  let token = sessionStorage.getItem(TOKEN_KEY);
  let orchestratorUrl = sessionStorage.getItem(ORCH_KEY);
  if (token === null || orchestratorUrl === null) {
    const created = await createSession();
    token = created.elementsToken;
    orchestratorUrl = created.orchestratorUrl;
    sessionStorage.setItem(TOKEN_KEY, token);
    sessionStorage.setItem(ORCH_KEY, orchestratorUrl);
  }

  const client = new NetworkSessionClient({ baseUrl: orchestratorUrl, elementsToken: token });

  await customElements.whenDefined("payplug-session");
  const session = document.querySelector<PayplugSession>("payplug-session");
  if (!session) return;

  // Merchant feature flags: restore from storage → apply to the session + the checkboxes, then
  // toggle live on change (the session is reactive — D-072).
  for (const flag of FLAGS) {
    const checkbox = document.getElementById(flag.id);
    if (!(checkbox instanceof HTMLInputElement)) continue;
    checkbox.checked = sessionStorage.getItem(flag.key) === "1";
    session.toggleAttribute(flag.attr, checkbox.checked);
    checkbox.addEventListener("change", () => {
      session.toggleAttribute(flag.attr, checkbox.checked);
      sessionStorage.setItem(flag.key, checkbox.checked ? "1" : "0");
    });
  }

  // ─── The language <select> (M10). The session default is navigator.language (D-090, no
  // `locale` attribute on the tag); a stored choice survives the redirect round-trip. Options
  // are sorted by endonym so a customer finds their language where they expect it.
  const localeSelect = document.querySelector<HTMLSelectElement>('[data-testid="locale-select"]');
  if (localeSelect) {
    const languages = [...SUPPORTED_LANGUAGES].sort((a, b) => endonym(a).localeCompare(endonym(b)));
    localeSelect.replaceChildren(
      ...languages.map((lang) => {
        const option = document.createElement("option");
        option.value = lang;
        option.textContent = `${endonym(lang)} (${lang})`;
        return option;
      })
    );
    const stored = sessionStorage.getItem(LOCALE_KEY);
    if (stored !== null) session.setAttribute("locale", stored);
    // Reflect the EFFECTIVE catalog (exact → base language → en, as i18n resolves it).
    const base = session.locale.split("-")[0] ?? session.locale;
    localeSelect.value = languages.includes(session.locale) ? session.locale : languages.includes(base) ? base : "en";
    localeSelect.addEventListener("change", () => {
      session.setAttribute("locale", localeSelect.value);
      sessionStorage.setItem(LOCALE_KEY, localeSelect.value);
    });
  }

  // Merchant reaction to the single routing signal (D-053/D-072): on a terminal status, the shop
  // shows its "return to shop" affordance — the elements never render it themselves.
  const merchantReturn = document.getElementById("merchant-return");
  const merchantLabel = document.getElementById("merchant-return-label");
  const terminalLabel: Record<string, string> = {
    PAID: "Commande validée.",
    ABORTED: "Paiement abandonné.",
    EXPIRED: "Session expirée.",
  };
  session.addEventListener("payplug:statuschange", (event) => {
    const detail = (event as CustomEvent<StatusChangeDetail>).detail;
    console.log(`payplug:statuschange ${detail.status}`, detail);
    const label = terminalLabel[detail.status];
    if (merchantReturn) merchantReturn.hidden = label === undefined;
    if (merchantLabel && label !== undefined) merchantLabel.textContent = label;
  });

  session.sessionClient = client;

  const reset = (): void => {
    sessionStorage.clear();
    location.href = "./m10.html";
  };
  document.querySelector('[data-testid="session-reset"]')?.addEventListener("click", reset);
  document.querySelector('[data-testid="merchant-restart"]')?.addEventListener("click", reset);
}

void main().catch((error: unknown) => {
  console.error(error);
});
